// TurtleModal.js - Turtle Graphics Scratch Pad using Skulpt
// Runs Python turtle code in the browser with visual canvas output

import React, { useState, useEffect, useRef } from 'react';
import {API_BASE_URL, apiFetch } from '../config';

const TurtleModal = ({ onClose }) => {
  const [code, setCode] = useState(() => {
    return localStorage.getItem('turtleScratchPadCode') || `import turtle

t = turtle.Turtle()
t.color("green")

# Draw a square
for i in range(4):
    t.forward(100)
    t.right(90)

t.done()`;
  });
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [skulptLoaded, setSkulptLoaded] = useState(false);
  const textareaRef = useRef(null);

  // Tutor Chat state (multi-turn conversation)
  const [showTutorChat, setShowTutorChat] = useState(false);
  const [tutorMessages, setTutorMessages] = useState([]);
  const [tutorInput, setTutorInput] = useState('');
  const [tutorLoading, setTutorLoading] = useState(false);
  const tutorChatRef = useRef(null);

  // Load Skulpt scripts
  useEffect(() => {
    const loadSkulpt = async () => {
      if (window.Sk && window.Sk.builtinFiles) {
        setSkulptLoaded(true);
        setOutput('🐢 Turtle ready! Click Run to execute your code.');
        return;
      }

      setOutput('⏳ Loading Skulpt library...');

      try {
        // Load Skulpt main from CDN
        if (!window.Sk) {
          const skulptScript = document.createElement('script');
          skulptScript.src = 'https://cdn.jsdelivr.net/npm/skulpt@1.2.0/dist/skulpt.min.js';
          skulptScript.crossOrigin = 'anonymous';
          document.head.appendChild(skulptScript);

          await new Promise((resolve, reject) => {
            skulptScript.onload = resolve;
            skulptScript.onerror = () => reject(new Error('Failed to load skulpt.min.js'));
          });
        }

        // Load Skulpt stdlib
        if (!window.Sk.builtinFiles) {
          const stdlibScript = document.createElement('script');
          stdlibScript.src = 'https://cdn.jsdelivr.net/npm/skulpt@1.2.0/dist/skulpt-stdlib.js';
          stdlibScript.crossOrigin = 'anonymous';
          document.head.appendChild(stdlibScript);

          await new Promise((resolve, reject) => {
            stdlibScript.onload = resolve;
            stdlibScript.onerror = () => reject(new Error('Failed to load skulpt-stdlib.js'));
          });
        }

        setSkulptLoaded(true);
        setOutput('🐢 Turtle ready! Click Run to execute your code.');
      } catch (error) {
        console.error('Skulpt load error:', error);
        setOutput('❌ Failed to load Skulpt library: ' + error.message + '\n\nPlease check your internet connection.');
      }
    };

    loadSkulpt();
  }, []);

  // Save code to localStorage
  useEffect(() => {
    localStorage.setItem('turtleScratchPadCode', code);
  }, [code]);

  // Focus textarea on mount
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isRunning) {
        onClose();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (!isRunning && skulptLoaded) {
          handleRun();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isRunning, skulptLoaded, code]);

  const handleRun = () => {
    if (!skulptLoaded || !window.Sk) {
      setOutput('❌ Skulpt not loaded yet. Please wait...');
      return;
    }

    if (!code.trim()) {
      setOutput('⚠️ Please enter some turtle code to run.');
      return;
    }

    setIsRunning(true);
    setOutput('🐢 Running...');

    // Configure Skulpt
    window.Sk.configure({
      output: (text) => {
        setOutput(prev => prev + text);
      },
      read: (filename) => {
        if (window.Sk.builtinFiles === undefined || 
            window.Sk.builtinFiles["files"][filename] === undefined) {
          throw "File not found: '" + filename + "'";
        }
        return window.Sk.builtinFiles["files"][filename];
      },
      __future__: window.Sk.python3,
    });

    // Set up turtle canvas - Skulpt needs the container div ID
    window.Sk.TurtleGraphics = window.Sk.TurtleGraphics || {};
    window.Sk.TurtleGraphics.target = 'turtle-canvas-container';
    window.Sk.TurtleGraphics.width = 1200;
    window.Sk.TurtleGraphics.height = 850;

    // Run the code
    window.Sk.misceval.asyncToPromise(() => {
      return window.Sk.importMainWithBody("<stdin>", false, code, true);
    }).then(
      () => {
        setOutput('✅ Done!');
        setIsRunning(false);
      },
      (err) => {
        setOutput('❌ Error: ' + err.toString());
        setIsRunning(false);
      }
    );
  };

  const handleClear = () => {
    // Clear the Skulpt-generated canvas
    const container = document.getElementById('turtle-canvas-container');
    if (container) {
      container.innerHTML = '';
    }
    setOutput('🐢 Canvas cleared. Ready to run!');
  };

  // Start Tutor Chat session
  const handleStartTutorChat = () => {
    setShowTutorChat(true);
    // Initial message from tutor
    if (tutorMessages.length === 0) {
      setTutorMessages([{
        role: 'assistant',
        content: `Hi! I'm your turtle graphics tutor. 🐢\n\nI can see your code above. What issue are you experiencing?\n\n1. What do you expect the code to do?\n2. What is it actually doing instead?`
      }]);
    }
  };

  // Send message to tutor
  const handleTutorSend = async () => {
    if (!tutorInput.trim() || tutorLoading) return;

    const userMessage = tutorInput.trim();
    setTutorInput('');
    setTutorMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setTutorLoading(true);

    // Build conversation history for API
    const conversationHistory = [
      ...tutorMessages.map(m => ({ role: m.role, content: m.content })),
      { role: 'user', content: userMessage }
    ];

    // Build system prompt with code context
    const outputSection = output ? `\n\nCurrent Output/Errors:\n\`\`\`\n${output}\n\`\`\`` : '';
    const systemPrompt = `You are a helpful Python tutor specializing in turtle graphics. 
You're helping a student debug their Python turtle code.

Current Student Code:
\`\`\`python
${code}
\`\`\`${outputSection}

Guidelines:
1. Focus on helping them understand and fix their turtle graphics code.
2. Ask them what they expect vs what's happening.
3. Guide them to use print statements to debug.
4. Give hints, not full solutions.
5. Work on one issue at a time.
6. Be encouraging and supportive.
7. Keep responses concise but helpful.
8. If output shows errors, help analyze them.`;

    try {
      const res = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: systemPrompt },
            ...conversationHistory
          ],
          temperature: 0.7,
          max_tokens: 500
        })
      });

      const data = await res.json();
      if (res.ok && data.choices && data.choices[0]) {
        const assistantMessage = data.choices[0].message.content;
        setTutorMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }]);
      } else {
        setTutorMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
      }
    } catch (err) {
      console.error('Error in tutor chat:', err);
      setTutorMessages(prev => [...prev, { role: 'assistant', content: 'Error connecting to tutor. Please try again.' }]);
    }
    setTutorLoading(false);

    // Scroll to bottom of chat
    setTimeout(() => {
      if (tutorChatRef.current) {
        tutorChatRef.current.scrollTop = tutorChatRef.current.scrollHeight;
      }
    }, 100);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      backdropFilter: 'blur(6px)',
      WebkitBackdropFilter: 'blur(6px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: '#161b22',
        borderRadius: '12px',
        border: '2px solid #15803d',
        width: '95%',
        maxWidth: '1600px',
        height: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid #30363d',
          backgroundColor: '#21262d',
          borderRadius: '12px 12px 0 0',
        }}>
          <span style={{ 
            color: '#4ade80', 
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '16px',
          }}>
            🐢 Turtle Graphics
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#8b949e', fontSize: '12px' }}>
              ⌘+Enter to run • Esc to close
            </span>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#f0f6fc',
                fontSize: '28px',
                cursor: 'pointer',
                padding: '2px 6px',
                borderRadius: '4px',
                lineHeight: 1,
                fontWeight: '600'
              }}
              onMouseOver={(e) => e.target.style.color = '#f85149'}
              onMouseOut={(e) => e.target.style.color = '#f0f6fc'}
            >
              ×
            </button>
          </div>
        </div>

        {/* Main Content - Side by Side */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left: Code Editor */}
          <div style={{ flex: '0 0 350px', display: 'flex', flexDirection: 'column', borderRight: '1px solid #fbbf24' }}>
            <div style={{ padding: '8px 12px', backgroundColor: '#21262d', borderBottom: '1px solid #30363d' }}>
              <span style={{ color: '#fbbf24', fontSize: '12px' }}>Python Code</span>
            </div>
            <textarea
              ref={textareaRef}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: '#0d1117',
                color: '#ffffff',
                border: '1px solid #fbbf24',
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: '16px',
                lineHeight: '1.5',
                resize: 'none',
                outline: 'none',
              }}
              spellCheck={false}
            />
            {/* Buttons */}
            <div style={{
              display: 'flex',
              gap: '8px',
              padding: '12px',
              backgroundColor: '#21262d',
              borderTop: '1px solid #30363d'
            }}>
              <button
                onClick={handleRun}
                disabled={isRunning || !skulptLoaded}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: isRunning ? '#484f58' : '#15803d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isRunning || !skulptLoaded ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                {isRunning ? '🐢 Running...' : '▶ Run Turtle'}
              </button>
              <button
                onClick={handleStartTutorChat}
                style={{
                  padding: '10px 16px',
                  backgroundColor: showTutorChat ? '#1f6feb' : '#1f6feb',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                {showTutorChat ? 'Tutor Chat' : 'Ask Tutor'}
              </button>
              <button
                onClick={handleClear}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#6e7681',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                🗑 Clear
              </button>
            </div>

            {/* Tutor Chat Panel */}
            {showTutorChat && (
              <div style={{
                backgroundColor: '#0d1117',
                display: 'flex',
                flexDirection: 'column',
                height: '280px',
                borderTop: '2px solid #1f6feb',
                flexShrink: 0
              }}>
                {/* Chat Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: '#161b22',
                  borderBottom: '1px solid #30363d'
                }}>
                  <span style={{ color: '#58a6ff', fontWeight: '600', fontSize: '13px' }}>
                    🎓 Turtle Tutor Chat
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => { setTutorMessages([]); }}
                      style={{
                        backgroundColor: 'transparent',
                        color: '#ffffff',
                        border: '1px solid #30363d',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '11px'
                      }}
                    >
                      Clear Chat
                    </button>
                    <button
                      onClick={() => { setShowTutorChat(false); setTutorMessages([]); }}
                      style={{
                        backgroundColor: 'transparent',
                        color: '#f85149',
                        border: 'none',
                        padding: '2px 6px',
                        cursor: 'pointer',
                        fontSize: '22px',
                        fontWeight: '600'
                      }}
                      onMouseOver={(e) => e.target.style.color = '#ff0000'}
                      onMouseOut={(e) => e.target.style.color = '#f85149'}
                    >
                      ✕
                    </button>
                  </div>
                </div>
                
                {/* Chat Messages */}
                <div
                  ref={tutorChatRef}
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '10px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}
                >
                  {tutorMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      style={{
                        alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                        maxWidth: '85%',
                        padding: '8px 12px',
                        borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                        backgroundColor: msg.role === 'user' ? '#166534' : '#21262d',
                        color: '#ffffff',
                        fontSize: '13px',
                        lineHeight: '1.5',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                    >
                      {msg.content}
                    </div>
                  ))}
                  {tutorLoading && (
                    <div style={{
                      alignSelf: 'flex-start',
                      padding: '8px 12px',
                      borderRadius: '12px 12px 12px 4px',
                      backgroundColor: '#21262d',
                      color: '#6e7681',
                      fontSize: '13px'
                    }}>
                      Thinking...
                    </div>
                  )}
                </div>
                
                {/* Chat Input */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  padding: '10px 12px',
                  borderTop: '1px solid #30363d',
                  backgroundColor: '#161b22'
                }}>
                  <textarea
                    value={tutorInput}
                    onChange={(e) => setTutorInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleTutorSend();
                      }
                    }}
                    placeholder="Ask the tutor for help... (Enter to send)"
                    rows={2}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      backgroundColor: '#0d1117',
                      color: '#ffffff',
                      border: '1px solid #30363d',
                      fontSize: '13px',
                      outline: 'none',
                      resize: 'none',
                      fontFamily: 'inherit',
                      lineHeight: '1.4'
                    }}
                  />
                  <button
                    onClick={handleTutorSend}
                    disabled={!tutorInput.trim() || tutorLoading}
                    style={{
                      backgroundColor: '#166534',
                      color: '#ffffff',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: (!tutorInput.trim() || tutorLoading) ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      opacity: (!tutorInput.trim() || tutorLoading) ? 0.6 : 1
                    }}
                  >
                    Send
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right: Canvas + Output */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Canvas Container - Skulpt creates canvas inside this */}
            <div style={{ flex: 1, padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0d1117' }}>
              <div
                id="turtle-canvas-container"
                style={{
                  width: '1200px',
                  height: '850px',
                  border: '2px solid #30363d',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  overflow: 'hidden'
                }}
              />
            </div>
            {/* Output */}
            <div style={{
              padding: '12px',
              backgroundColor: '#0d1117',
              borderTop: '1px solid #30363d',
              maxHeight: '120px',
              overflowY: 'auto'
            }}>
              <div style={{
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: '13px',
                color: '#e6d88a',
                whiteSpace: 'pre-wrap'
              }}>
                {output || <span style={{ color: '#8b949e' }}>Output will appear here...</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TurtleModal;
